import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LANGUAGES } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { ACCOUNT, TOUR } from "@/constants/testIds";
import SafetyNetBanner from "@/components/SafetyNetBanner";
import ContactDialog from "@/components/ContactDialog";
import ReferralProgress from "@/components/ReferralProgress";
import SignedDocuments from "@/components/SignedDocuments";
import ChangePasswordForm from "@/components/ChangePasswordForm";
import DiscordLinkCard from "@/components/DiscordLinkCard";
import MonthlyRecapToggle from "@/components/MonthlyRecapToggle";
import LearningRemindersToggle from "@/components/LearningRemindersToggle";
import PrivacyDataCard from "@/components/PrivacyDataCard";
import AddonPaymentAlert from "@/components/AddonPaymentAlert";
import { Compass } from "lucide-react";

export default function Account() {
  const { t } = useTranslation();
  const { user, refresh, logout, updateProfile } = useAuth();
  const { setLang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prefLang, setPrefLang] = useState(user?.preferred_language || "en");
  const [savingLang, setSavingLang] = useState(false);
  const [replayingTour, setReplayingTour] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  // Deep-link support — sidebar/email CTAs pass `?tab=discord` (or
  // `?tab=documents`, `?tab=referral`, `?tab=security`) so users land
  // ON the relevant section instead of the top of the page. Scrolls
  // once after mount + short delay so the section has rendered.
  const discordRef  = useRef(null);
  const docsRef     = useRef(null);
  const referralRef = useRef(null);
  const securityRef = useRef(null);
  const billingRef  = useRef(null);
  const privacyRef  = useRef(null);
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    const targetRef = ({
      discord:  discordRef,
      documents: docsRef,
      referral:  referralRef,
      security:  securityRef,
      billing:   billingRef,
      privacy:   privacyRef,
    })[tab];
    if (!targetRef) return;
    const timer = setTimeout(() => {
      targetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [searchParams]);

  if (!user || typeof user !== "object") return null;

  const replayTour = async () => {
    setReplayingTour(true);
    try {
      await api.post("/auth/tour/reset");
      await refresh();
      // Navigate to the dashboard with ?tour=1 so the tour restarts even
      // though the reset itself would trigger auto-start on next mount.
      navigate("/dashboard?tour=1");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setReplayingTour(false);
    }
  };

  const saveLanguage = async () => {
    setSavingLang(true);
    try {
      await updateProfile({ preferred_language: prefLang });
      setLang(prefLang);
      toast.success(t("account.languageSaved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingLang(false);
    }
  };

  return (
    <div data-testid={ACCOUNT.root} className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
          {t("account.eyebrow")}
        </div>
        <h1 className="font-display font-bold text-4xl text-slate-900 tracking-tight">
          {t("account.title")}
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("account.name")}</div>
          <div className="mt-1 text-slate-900 font-medium">{user.name}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("account.email")}</div>
          <div className="mt-1 text-slate-900 font-medium">{user.email}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("account.role")}</div>
          <div className="mt-1 text-slate-900 font-medium capitalize">{user.role}</div>
        </div>
      </div>

      {/* Language preference */}
      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">
          {t("account.languageTitle")}
        </div>
        <p className="mt-2 text-sm text-slate-600">
          {t("account.languageBody")}
        </p>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <Select value={prefLang} onValueChange={setPrefLang}>
            <SelectTrigger
              data-testid={ACCOUNT.languageSelect}
              className="w-56 h-11 bg-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code}>
                  <span className="flex items-center gap-2">
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid={ACCOUNT.languageSaveButton}
            onClick={saveLanguage}
            disabled={savingLang || prefLang === user.preferred_language}
            className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6"
          >
            {savingLang ? t("account.saving") : t("account.save")}
          </Button>
        </div>
      </div>

      <MonthlyRecapToggle />

      <LearningRemindersToggle />

      <div ref={billingRef} style={{ scrollMarginTop: 24 }}>
        <AddonPaymentAlert />
      </div>

      <SafetyNetBanner onRefunded={refresh} />

      <div ref={securityRef} style={{ scrollMarginTop: 24 }}>
        <ChangePasswordForm />
      </div>

      <div ref={discordRef} style={{ scrollMarginTop: 24 }}>
        <DiscordLinkCard />
      </div>

      <div ref={docsRef} style={{ scrollMarginTop: 24 }}>
        <SignedDocuments />
      </div>

      <div ref={referralRef} style={{ scrollMarginTop: 24 }}>
        <ReferralProgress />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">{t("account.membership")}</div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="font-display font-black text-3xl text-slate-900 capitalize">{t(`tier.${user.tier}`)}</div>
          {/* Only show status for PAID tiers — "inactive" on Free tier
              was confusing (nothing to be inactive from). Show status
              only when it's actually meaningful. */}
          {user.tier !== "free" && user.subscription_status && (
            <div className="text-sm text-slate-500 uppercase tracking-widest">
              {user.subscription_status}
            </div>
          )}
        </div>
        <p className="mt-3 text-sm text-slate-600">
          {user.tier === "premium"
            ? t("account.premiumNote")
            : user.tier === "basic"
              ? t("account.basicNote")
              : t("account.freeNote")}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {user.tier !== "premium" && (
            <Button
              data-testid={ACCOUNT.upgradeButton}
              onClick={() => navigate("/pricing")}
              className="bg-blue-800 hover:bg-blue-700 text-white h-11 px-6"
            >
              {t("account.upgradePlan")}
            </Button>
          )}
        </div>
        {user.tier !== "free" && (
          <p className="mt-4 text-xs text-slate-500">
            {t("account.oneTimeNote")}{" "}
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="text-blue-800 font-medium hover:underline"
              data-testid="account-support-email"
            >
              {t("account.contactSupport")}
            </button>{" "}
            {t("account.contactSupportSuffix")}
          </p>
        )}
      </div>

      <ContactDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        subject={`Membership question · ${user?.email || ""}`}
      />

      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("account.tourTitle")}</div>
        <p className="mt-2 text-sm text-slate-600 max-w-lg">
          {t("account.tourBody")}
        </p>
        <Button
          data-testid={TOUR.restartButton}
          onClick={replayTour}
          disabled={replayingTour}
          variant="outline"
          className="mt-4 border-slate-300 h-10 px-5 disabled:opacity-60"
        >
          <Compass size={15} className="mr-2" />
          {replayingTour ? t("account.tourStarting") : t("account.tourRestart")}
        </Button>
      </div>

      <div ref={privacyRef} style={{ scrollMarginTop: 24 }}>
        <PrivacyDataCard />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-8">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("account.signOut")}</div>
        <p className="mt-2 text-sm text-slate-600">{t("account.signOutBody")}</p>
        <Button
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          variant="outline"
          className="mt-4 border-slate-300 h-10 px-5"
        >
          {t("account.logOut")}
        </Button>
      </div>
    </div>
  );
}
